<template>
  <div class="sitemap-page">
    <Navbar />

    <!-- 頂部大氣橫幅 Banner -->
    <header class="sitemap-banner">
      <div class="sitemap-banner-inner">
        <h1 class="banner-title">網站地圖 (Site Map)</h1>
        <p class="banner-subtitle">
          本站依無障礙網頁設計原則建置，提供便捷快速鍵與完整的全站架構導引
        </p>
      </div>
    </header>

    <!-- 內容主體 -->
    <main id="main-content" class="sitemap-main" tabindex="-1">
      <a
        id="accesskey-c"
        href="#accesskey-c"
        accesskey="C"
        class="accesskey-brick sr-only-focusable"
        title="主要內容區塊，快捷鍵：Alt+C"
        aria-label="主要內容區塊"
      >:::</a>
      <div class="sitemap-wrapper">
        <!-- 麵包屑導航 -->
        <nav aria-label="麵包屑導航" class="sitemap-breadcrumb">
          <ol class="breadcrumb-list">
            <li><router-link to="/home">首頁</router-link></li>
            <li aria-hidden="true" class="separator">/</li>
            <li aria-current="page">網站地圖</li>
          </ol>
        </nav>

        <!-- 快速鍵指南卡片 -->
        <div class="accesskey-guide-banner" role="region" aria-label="快速鍵操作指引">
          <div class="guide-badge">
            <span class="badge-icon">⌨️</span>
            <span class="badge-text">無障礙快速鍵操作指南</span>
          </div>
          <p class="guide-desc">
            本網站依無障礙網頁設計原則規劃，提供便捷鍵盤快速鍵（Accesskey）：
          </p>
          <div class="guide-grid">
            <div class="guide-item">
              <div class="key-tag">Alt + U</div>
              <div class="key-info">
                <strong>上方導覽區塊：</strong>移至主要導覽列與網站標誌
              </div>
            </div>
            <div class="guide-item">
              <div class="key-tag">Alt + C</div>
              <div class="key-info">
                <strong>主要內容區塊：</strong>移至各頁面主要資訊內容
              </div>
            </div>
            <div class="guide-item">
              <div class="key-tag">Alt + Z</div>
              <div class="key-info">
                <strong>下方頁尾區塊：</strong>移至頁尾資訊與版權宣告
              </div>
            </div>
          </div>
          <p class="mac-hint">
            💡 Mac 系統使用者請使用 <code>Control + Option + 快速鍵代碼</code>
          </p>
        </div>

        <!-- 網站架構卡片網格 -->
        <section class="sitemap-grid" aria-label="全站單元架構">
          <!-- 1. 最新消息 -->
          <div class="category-card">
            <div class="card-header">
              <h2 class="card-title">最新消息</h2>
            </div>
            <ul class="link-list">
              <li><router-link to="/activities">活動與公告列表</router-link></li>
            </ul>
          </div>

          <!-- 2. 簡介與沿革 -->
          <div class="category-card">
            <div class="card-header">
              <h2 class="card-title">簡介與沿革</h2>
            </div>
            <ul class="link-list">
              <li><router-link to="/minister-intro">部長序</router-link></li>
              <li><router-link to="/vice-minister-intro">次長序</router-link></li>
              <li><router-link to="/director-intro">處長序</router-link></li>
            </ul>
          </div>

          <!-- 3. 標準介紹與學習 -->
          <div class="category-card">
            <div class="card-header">
              <h2 class="card-title">標準介紹與認證</h2>
            </div>
            <ul class="link-list">
              <li><router-link to="/loinc">什麼是 LOINC</router-link></li>
              <li><router-link to="/loinc-education">LOINC 學習資源</router-link></li>
              <li><router-link to="/workshop-courses">工作坊課程</router-link></li>
              <li><router-link to="/workshop-materials">培訓教材下載</router-link></li>
              <li><router-link to="/certification-apply">認證申請說明</router-link></li>
            </ul>
          </div>

          <!-- 4. 臺灣標準專區 -->
          <div class="category-card">
            <div class="card-header">
              <h2 class="card-title">臺灣標準專區</h2>
            </div>
            <ul class="link-list">
              <li><router-link to="/standard-announcements">標準公告發布</router-link></li>
              <li><router-link to="/loinc-proposal-form">標準提案申請</router-link></li>
            </ul>
          </div>

          <!-- 5. 會員中心與支援 -->
          <div class="category-card">
            <div class="card-header">
              <h2 class="card-title">會員中心與支援</h2>
            </div>
            <ul class="link-list">
              <li><router-link to="/sign-in">會員登入</router-link></li>
              <li><router-link to="/register">免費註冊</router-link></li>
              <li><router-link to="/dashboard">學習儀表板</router-link></li>
              <li><router-link to="/customer-service">客服支援中心</router-link></li>
            </ul>
          </div>

          <!-- 6. 權益政策與規範 -->
          <div class="category-card">
            <div class="card-header">
              <h2 class="card-title">權益政策與規範</h2>
            </div>
            <ul class="link-list">
              <li><router-link to="/privacy">隱私權保護及資訊安全政策</router-link></li>
              <li><router-link to="/terms">會員服務條款</router-link></li>
            </ul>
          </div>
        </section>
      </div>
    </main>

    <FooterSection />
  </div>
</template>

<script setup>
import Navbar from '@/components/Navbar.vue';
import FooterSection from '@/components/FooterSection.vue';
</script>

<style scoped>
.sitemap-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f8fafc;
}

.sitemap-banner {
  background: linear-gradient(135deg, #022964 0%, #004085 100%);
  padding: 44px 24px;
  color: #ffffff;
}

.sitemap-banner-inner {
  max-width: 1200px;
  margin: 0 auto;
}

.banner-title {
  font-size: 2.25rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 12px;
  letter-spacing: 0.5px;
}

.banner-subtitle {
  font-size: 1.0625rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  line-height: 1.6;
}

.sitemap-main {
  flex: 1;
}

.sitemap-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px 64px;
  box-sizing: border-box;
  display: block !important;
}

/* 麵包屑 */
.sitemap-breadcrumb {
  margin-bottom: 24px;
}

.breadcrumb-list {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9375rem;
  color: #64748b;
  margin: 0;
  padding: 0;
  list-style: none;
}

.breadcrumb-list a {
  color: #005a9c;
  text-decoration: none;
}

.breadcrumb-list a:hover {
  text-decoration: underline;
}

.separator {
  color: #94a3b8;
}

/* 快速鍵指南卡片 */
.accesskey-guide-banner {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 24px 28px;
  margin-bottom: 36px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}

.guide-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 12px;
}

.guide-desc {
  font-size: 0.95rem;
  color: #475569;
  margin: 0 0 16px;
  line-height: 1.6;
}

.guide-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.guide-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8fafc;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.key-tag {
  background: #005a9c;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.875rem;
  padding: 4px 10px;
  border-radius: 6px;
  white-space: nowrap;
  letter-spacing: 0.5px;
}

.key-info {
  font-size: 0.875rem;
  color: #334155;
  line-height: 1.5;
}

.mac-hint {
  font-size: 0.8125rem;
  color: #64748b;
  margin: 16px 0 0;
  padding-top: 12px;
  border-top: 1px dashed #e2e8f0;
}

.mac-hint code {
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  color: #0f172a;
  font-weight: 600;
}

/* 網站架構卡片網格 */
.sitemap-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

@media (max-width: 992px) {
  .sitemap-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .sitemap-grid {
    grid-template-columns: 1fr;
  }
}

.category-card {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
}

.category-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

.card-header {
  border-bottom: 2px solid #005a9c;
  padding-bottom: 12px;
  margin-bottom: 16px;
}

.card-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.link-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.link-list li {
  position: relative;
  padding-left: 16px;
}

.link-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  top: 0;
  color: #005a9c;
  font-size: 1.1rem;
  line-height: 1.4;
}

.link-list a {
  color: #334155;
  text-decoration: none;
  font-size: 0.9375rem;
  font-weight: 500;
  display: inline-block;
  transition: color 0.2s ease, transform 0.2s ease;
}

.link-list a:hover,
.link-list a:focus-visible {
  color: #005a9c;
  transform: translateX(4px);
  text-decoration: underline;
}
</style>
